/**
 * Provisioning a tenant, and what the server said about it.
 *
 * @module components/platform/useCreateTenant
 */

import { useState } from "react";

import type { CreateTenantRequest } from "@/components/platform/CreateTenantForm";
import { parseAddressList } from "@/components/shared/address-list";
import type {
  CreateTenantInput,
  ProgrammePlatformClient,
  TenantOnboardingSummary,
} from "@/platform";

export interface CreateTenantState {
  pending: boolean;
  error: string | null;
  created: TenantOnboardingSummary | null;
  clearError: () => void;
  /** Resolves true when the tenant exists, so the form can clear itself. */
  create: (request: CreateTenantRequest) => Promise<boolean>;
}

/**
 * @param client - The platform client to write through.
 * @param onCreated - Reloads the directory, so the new tenant is selectable.
 * @returns The write, and what it said.
 *
 * @example
 * ```tsx
 * const creation = useCreateTenant(client, directory.reload);
 * ```
 */
export function useCreateTenant(
  client: ProgrammePlatformClient,
  onCreated: () => Promise<void>,
): CreateTenantState {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<TenantOnboardingSummary | null>(null);

  const create = async (request: CreateTenantRequest): Promise<boolean> => {
    setPending(true);
    setError(null);
    setCreated(null);
    const alternateEmails = parseAddressList(request.alternateEmails);
    const displayName = request.form.adminDisplayName?.trim();
    const input: CreateTenantInput = {
      name: request.form.name.trim(),
      slug: request.form.slug.trim().toLowerCase(),
      adminEmail: request.form.adminEmail.trim().toLowerCase(),
      ...(alternateEmails.length ? { adminAlternateEmails: alternateEmails } : {}),
      ...(displayName ? { adminDisplayName: displayName } : {}),
    };
    try {
      setCreated(await client.createTenant(input));
      await onCreated();
      return true;
    } catch (cause) {
      setCreated(null);
      setError(cause instanceof Error ? cause.message : "Unable to create the organisation");
      return false;
    } finally {
      setPending(false);
    }
  };

  return {
    pending,
    error,
    created,
    clearError: () => {
      setError(null);
    },
    create,
  };
}
